import { useEffect, useRef, type RefObject } from 'react';

/**
 * Custom hook to handle click outside events for dropdowns, modals, and menus.
 *
 * @template T - The HTML element type (e.g., HTMLDivElement, HTMLElement)
 * @param handler - Callback function to execute when clicking outside the element
 * @param options - Optional configuration
 * @param options.enabled - Whether the click outside detection is enabled (default: true)
 * @returns A ref object to attach to the element
 *
 * @example
 * ```tsx
 * const menuRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false));
 * return <div ref={menuRef}>Menu content</div>
 * ```
 *
 * @example
 * ```tsx
 * // Conditionally enable
 * const modalRef = useClickOutside<HTMLDivElement>(
 *   () => setIsOpen(false),
 *   { enabled: isOpen }
 * );
 * ```
 */
export function useClickOutside<T extends HTMLElement>(
  handler: () => void,
  options: { enabled?: boolean } = {}
): RefObject<T> {
  const ref = useRef<T>(null);
  const { enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;

    function handleClickOutside(event: Event) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handler, enabled]);

  return ref;
}
